import { compact } from 'lodash';
import { ReactElement, useMemo } from 'react';
import styled from 'styled-components';

import { Bean, MoneyPatternType } from '@dreryk/react-common';
import {
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
   Text,
   TextSeparator,
   TextSeparatorType,
   TooltipText,
   ValueRendererProps,
} from '@dreryk/react-components';

import { protectFeature } from '~libs.shared.system';

import { visitSettlementTranslationKeys } from '../../translations';
import { VisitSettlementControl, VisitSettlementRegister } from '../../visitSettlement.types';
import { getVisitSettlementValue } from '../../visitSettlement.utils';
import { PriceText } from '../price/PriceText';

import { MedicalServiceBean, VisitMedicalServiceTableBean } from './medicalServices.types';
import { MedicalServiceTableSelector } from './MedicalServiceTableSelector';
import {
   StyledBottomElement,
   StyledVisitMedicalServiceInteractiveTable,
} from './VisitMedicalServiceInteractiveTable.styled';

const StyledPriceText = styled(PriceText)`
   color: ${(props) => props.theme.textBody};
   text-align: right;
   display: block;
`;

StyledPriceText.displayName = 'StyledPriceText';

type UseColumnsConfigurationOptions = {
   editable?: boolean;
};

// eslint-disable-next-line max-lines-per-function
const useColumnsConfiguration = (
   options: UseColumnsConfigurationOptions
): InteractiveTableColumnConfiguration<VisitMedicalServiceTableBean>[] => {
   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         medicalService: '100%',
         price: '110px',
         options: options.editable ? '30px' : '0px',
      });

      return compact([
         {
            name: 'medicalService',
            label: visitSettlementTranslationKeys.shared.medicalServices.table.headers.name,
            width: columnWidths.medicalService,

            renderer: ({ bean, index }: ValueRendererProps<VisitMedicalServiceTableBean>) => {
               return (
                  <InteractiveTable.CustomCell<MedicalServiceBean>
                     testId={'medicalService'}
                     name={'medicalService'}
                     index={index}
                     defaultValue={bean.medicalService}
                  >
                     {({ name, priceListPrice }) => (
                        <TooltipText testId={'medicalService'}>
                           <TextSeparator separator={TextSeparatorType.SPACE}>
                              <Text testId={'name'}>{name}</Text>
                              <PriceText testId={'priceListPrice'} value={priceListPrice} stripDecimalZeroes />
                           </TextSeparator>
                        </TooltipText>
                     )}
                  </InteractiveTable.CustomCell>
               );
            },
         },
         {
            name: 'price',
            label: visitSettlementTranslationKeys.shared.medicalServices.table.headers.price,
            width: columnWidths.price,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,

            renderer: ({ bean, index }: ValueRendererProps<VisitMedicalServiceTableBean>): JSX.Element | null => {
               if (options.editable) {
                  return (
                     <InteractiveTable.MoneyInputCell
                        testId={'price'}
                        name={'price'}
                        index={index}
                        validationRules={{
                           required: true,
                        }}
                        defaultValue={bean.price}
                        pattern={MoneyPatternType.POSITIVE}
                        min={1}
                        max={999999.99}
                        minSize={3}
                        maxSize={9}
                     />
                  );
               }

               return (
                  <InteractiveTable.CustomCell<number>
                     testId={'price'}
                     name={'price'}
                     index={index}
                     defaultValue={bean.price}
                  >
                     {(value) => <StyledPriceText testId={'price'} value={value} stripDecimalZeroes />}
                  </InteractiveTable.CustomCell>
               );
            },
         },
         options.editable && {
            name: 'options',
            width: columnWidths.options,

            renderer: ({ index }: ValueRendererProps<VisitMedicalServiceTableBean>) => {
               return <InteractiveTable.DeleteRowCell index={index} />;
            },
         },
      ]);
   }, [options.editable]);
};

type VisitMedicalServiceInteractiveTableProps = {
   readonly register: VisitSettlementRegister;
   readonly control: VisitSettlementControl;
   readonly practitionerId: number;
   readonly bottomElement?: ReactElement;
};

export const VisitMedicalServiceInteractiveTable = (props: VisitMedicalServiceInteractiveTableProps): JSX.Element => {
   const { editable } = getVisitSettlementValue(props.control);
   const columns = useColumnsConfiguration({
      editable: editable,
   });

   return (
      <StyledVisitMedicalServiceInteractiveTable>
         <InteractiveTable<VisitMedicalServiceTableBean, Bean>
            name={'visitSettlement.medicalServices'}
            register={props.register}
            columns={columns}
            testId={'visitMedicalService'}
            header={protectFeature(
               'searchingPrivateMedicalServices',
               <MedicalServiceTableSelector
                  register={props.register}
                  practitionerId={props.practitionerId}
                  editable={editable}
               />
            )}
            placeholder={
               editable ? (
                  <EmptyStatePlaceholder
                     heading={visitSettlementTranslationKeys.shared.medicalServices.table.placeholder.editable.heading}
                     subheading={
                        visitSettlementTranslationKeys.shared.medicalServices.table.placeholder.editable.subheading
                     }
                     testId={'noResults'}
                     fullLength
                  />
               ) : (
                  <EmptyStatePlaceholder
                     heading={
                        visitSettlementTranslationKeys.shared.medicalServices.table.placeholder.noEditable.heading
                     }
                     subheading={
                        visitSettlementTranslationKeys.shared.medicalServices.table.placeholder.noEditable.subheading
                     }
                     testId={'settledVisit'}
                     fullLength
                  />
               )
            }
            autoScroll={'start'}
         />
         {props.bottomElement ? <StyledBottomElement>{props.bottomElement}</StyledBottomElement> : null}
      </StyledVisitMedicalServiceInteractiveTable>
   );
};
